import { useRef, type ReactNode } from 'react';
import { Sheet as SheetRoot, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { IconArrowRight } from '../icons';
import { Button } from './Button';

/**
 * Right-side panel — the SONAR anatomy (POSidePanel/TaskSidePanel reference, 104):
 * header (title + status badge + actions) → optional tabs strip → sectioned scrolling
 * body → footer action. Row click opens a quick preview; the footer's full-width action
 * navigates to the URL-real detail page (PRD v2 §3 — SheetOpenFull stays the footer
 * idiom). Also reused for quick-create and edit forms where context must stay visible.
 *
 * Rebuilt on the vendored shadcn/Radix sheet primitives (102-era hand-rolled trap
 * retired): Radix owns the focus trap, Esc, overlay click, and the body scroll lock.
 *
 * ⚠️ Focus restore is OWNED here, not left to Radix. Under React 19.1 Radix's own
 * close restore lands on <body> (the same regression the 101 drawer hit), and this
 * panel's 28 call sites cannot each thread an opener ref. The opener is captured at
 * the open TRANSITION, during render — deliberately NOT in Radix's `onOpenAutoFocus`:
 * Radix skips that dispatch entirely when a child `autoFocus` field already holds
 * focus by the time its effect runs, which is every EditSheet form, and a capture
 * that sometimes never fires restores nothing (review M1 — measured: Esc from Edit
 * customer landed on <body>). At the render where `open` flips true the activeElement
 * is still the opener, because the state change came from the opener's own
 * click/keypress. `onCloseAutoFocus` prevents Radix's default and restores it. The
 * browser probe asserts the round-trip on BOTH a preview and an autoFocus form
 * (assets/104).
 */
export function Sheet({
  open,
  onClose,
  title,
  badge,
  actions,
  tabs,
  children,
  footer,
  wide,
  className = '',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  badge?: ReactNode;
  /** Header action buttons, right of the title block (the Sonar header anatomy). */
  actions?: ReactNode;
  /**
   * A tabs strip between header and body — rendered verbatim. In here, pass
   * `<UnderlineTabList flush>`: the header above already draws the full-width rule,
   * and a strip that brings its own renders two gray rules 13px apart (104 m5).
   */
  tabs?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Sonar-format 560px, for bodies that earn it (line builders, tabbed content). */
  wide?: boolean;
  /** Optional feature-scoped styles for the content and its descendant controls. */
  className?: string;
}) {
  const opener = useRef<HTMLElement | null>(null);
  const openerHome = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  // A ref write during render, guarded to the false→true transition — idempotent under
  // a StrictMode double-render (same activeElement both times). A useLayoutEffect is
  // already too late: React commits a child's autoFocus before parent layout effects
  // run, so the activeElement would be the field inside the panel, not the opener.
  if (open && !wasOpen.current) {
    opener.current = document.activeElement as HTMLElement | null;
    // …and its landmark, for when the opener itself is GONE by close time (below).
    openerHome.current = opener.current?.closest('main') ?? null;
  }
  wasOpen.current = open;

  return (
    <SheetRoot open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <SheetContent
        side="right"
        // No SheetDescription: the body IS the description, and Radix's warning wants
        // the attribute silenced explicitly rather than pointed at nothing.
        aria-describedby={undefined}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          const el = opener.current;
          if (el?.isConnected) {
            el.focus();
            return;
          }
          // The opener can be gone by close time — triage approves a request and the
          // refetch removes its row during the exit animation, so focusing the
          // detached node is a silent no-op and focus falls to <body> (Codex 104-2).
          // The opener's landmark is the stable fallback: the skip-link pattern,
          // tabindex=-1 then focus, so Tab resumes from the content, not the top.
          const home = openerHome.current;
          if (home?.isConnected) {
            home.setAttribute('tabindex', '-1');
            home.focus();
          }
        }}
        /**
         * ⚠️ The bottom inset, not `0`. `index.html` sets `viewport-fit=cover`, so on a
         * notched phone the last 34px of this panel are UNDER the home indicator — which is
         * exactly where the footer's primary action sits, and every one of this panel's call
         * sites puts its action there. `TableScrollRail.tsx` already carries the same line
         * for the same reason; this is the second thing in the app pinned to the bottom of
         * the viewport, and it lands on the WRAPPER so all its call sites get it at once
         * rather than each remembering.
         *
         * `env(...)` resolves to `0px` on every desktop browser, so nothing above 768
         * changes by a pixel. Measured at 390 in the browser probe (assets/114).
         *
         * ⚠️ Via `--sw-safe-bottom` since 116, not the `env()` longhand. Same value, one
         * definition — and the variable is what makes the inset MEASURABLE: `env()` is 0 in
         * a headless browser, so a probe that reads this computed style is proving nothing.
         * Overriding the variable drives a real inset through here and the rail at once.
         *
         * The panel is portaled to `<body>`, so it reads `:root` — deliberately NOT the
         * shell's `--sw-bottom-chrome`. This is a full-height overlay ABOVE the tab bar,
         * not a page competing with it for the bottom edge.
         */
        style={{ paddingBottom: 'var(--sw-safe-bottom)' }}
        className={`${wide ? 'w-[560px]' : 'w-[420px]'} max-w-[92vw] gap-0 border-border bg-surface p-0 shadow-[var(--shadow-lift)] sm:max-w-[92vw] ${className}`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border p-4 pr-12">
          <div className="min-w-0">
            <SheetTitle className="truncate font-heading text-lg font-semibold text-text">
              {title}
            </SheetTitle>
            {badge ? <div className="mt-1">{badge}</div> : null}
          </div>
          {actions ? (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          ) : null}
        </div>
        {tabs ? <div className="shrink-0 px-4">{tabs}</div> : null}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        {footer ? (
          <div className="shrink-0 border-t border-border p-4">{footer}</div>
        ) : null}
      </SheetContent>
    </SheetRoot>
  );
}

/** The Sheet's standard footer action: full-width navigate-to-detail. */
export function SheetOpenFull({
  onClick,
  label = 'Open full page',
}: {
  onClick: () => void;
  label?: string;
}) {
  return (
    <Button onClick={onClick} className="w-full">
      {label}
      <IconArrowRight size={16} />
    </Button>
  );
}
